import { convertFromRaw } from 'draft-js';
import * as React from 'react';

import { useAnalyticsLogger } from '@/imports/ui/amplitude/helpers';

import { EEditState, EEditStateMsg, IBlogForm } from '../../BlogEditModal';
import { useCreateBlog } from './hooks/useCreateBlog';
import { useUpdateBlog } from './hooks/useUpdateBlog';

type SaveBlogAsyncOptions = {
  postId?: string;
  setPostId: React.Dispatch<React.SetStateAction<string | undefined>>;
  blog: IBlogForm;
  alreadyPublished: boolean;
  publish: boolean;
  isAnnouncement: boolean;
};

export const useSaveBlogAsync = (options: {
  setEditState: React.Dispatch<
    React.SetStateAction<{
      state: EEditState | null;
      msg: string;
    }>
  >;
}): {
  saveBlogAsync: (options: SaveBlogAsyncOptions) => Promise<boolean>;
} => {
  const { setEditState } = options;

  const { createBlog } = useCreateBlog();

  const { updateBlog } = useUpdateBlog();

  const analyticsClient = useAnalyticsLogger();

  const saveBlogAsync = React.useCallback(
    async (options: SaveBlogAsyncOptions) => {
      const {
        blog,
        publish,
        alreadyPublished,
        isAnnouncement,
        postId,
        setPostId,
      } = options;
      if (
        blog.title === '' &&
        convertFromRaw(blog.content).getPlainText() === '' &&
        blog.bannerImage?.imageId === undefined
      ) {
        return true;
      }

      setEditState({
        state: EEditState.SAVING,
        msg: publish ? 'Publishing...' : EEditStateMsg[EEditState.SAVING],
      });

      if (blog.title === '' && publish === true) {
        // TODO: This is bugged and doesn't perform any validation
        setEditState({
          state: EEditState.FAILED,
          msg: 'Title is required',
        });
        return false;
      }

      let res;
      if (postId) {
        res = await updateBlog({
          postId,
          blog: {
            title: blog.title,
            content: blog.content,
            tagIds: blog.tags?.map(
              (tag: { _id: string; name: string }) => tag._id
            ),
            groupId: blog.group,
            blogBannerImageId: blog.bannerImage?.imageId ?? null,
            scheduledPublishDate:
              blog.scheduledPublishDate?.toISOString() ?? null,
          },
          options: {
            analyticsClient,
            publish,
            alreadyPublished,
            isAnnouncement,
          },
        });
      } else {
        res = await createBlog({
          setPostId,
          blog: {
            title: blog.title,
            content: blog.content,
            tagIds: blog.tags?.map(
              (tag: { _id: string; name: string }) => tag._id
            ),
            organizationId: blog.organization,
            ...(blog.organization && blog.group
              ? {
                  sponsorData: {
                    isSponsoredPost: true,
                    audience: { groupId: blog.group },
                  },
                }
              : { groupId: blog.group }),
            blogBannerImageId: blog.bannerImage?.imageId,
          },
          options: {
            analyticsClient,
            publish,
            alreadyPublished,
            isAnnouncement,
            scheduledPublishDate: blog.scheduledPublishDate,
          },
        });
      }

      if (!res) {
        setEditState({
          state: EEditState.FAILED,
          msg: `Failed to ${publish ? 'publish' : 'save'} blog`,
        });
        return false;
      }

      setEditState({
        state: EEditState.SAVED,
        msg: 'Blog saved',
      });

      return true;
    },
    [analyticsClient, createBlog, setEditState, updateBlog]
  );

  return {
    saveBlogAsync,
  };
};
